/**
 * The password rule — one rule for every portal and every screen that makes a
 * password (sign-up, reset, change, set).
 *
 * NIST SP 800-63B §5.1.1.2: length is what makes a password hard to guess, so
 * the rule is a minimum length and nothing else — no forced mix of capitals,
 * digits and symbols, which pushes people to `Password1!` and makes nothing
 * safer. A long passphrase is welcome. Passwords known from data breaches are
 * refused by Clerk (HIBP), which the screens show as `password_breached`.
 *
 * The Clerk instances enforce the same minimum (Dashboard → User & authentication
 * → Password → Minimum length), so a client that skipped this schema would
 * still be refused. The maximum is Clerk's: bcrypt reads 72 bytes.
 *
 * Signing IN never applies this rule — see `loginSchema`: an account made
 * under an older rule must keep working.
 */

import { z } from 'zod';

export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 72;

/** A new password. Messages are locale keys. */
export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, 'validation.password.min')
  .max(PASSWORD_MAX_LENGTH, 'validation.password.max');

/** Whether a password being typed meets the rule — for the live hint under the field. */
export function meetsPasswordRule(password: string): boolean {
  return passwordSchema.safeParse(password).success;
}

const confirmMatches = <T extends { newPassword: string; confirmPassword: string }>(data: T) =>
  data.newPassword === data.confirmPassword;

const MISMATCH = { message: 'validation.password.mismatch', path: ['confirmPassword'] };

/** Set a first password — an account made with Apple, Google, Microsoft or LinkedIn. */
export const setPasswordSchema = z
  .object({
    newPassword: passwordSchema,
    confirmPassword: z.string().min(1, 'validation.password.confirmRequired'),
    /** Sign out every other device once the password is set. */
    signOutOfOtherSessions: z.boolean(),
  })
  .refine(confirmMatches, MISMATCH);

export type SetPasswordInput = z.infer<typeof setPasswordSchema>;

/** Change the password — the current one proves it is you. */
export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'validation.password.required'),
    newPassword: passwordSchema,
    confirmPassword: z.string().min(1, 'validation.password.confirmRequired'),
    /** Sign out every other device once the password changes. */
    signOutOfOtherSessions: z.boolean(),
  })
  .refine(confirmMatches, MISMATCH);

export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;
